/**
 * Auto Free Gift — Simple One-Time Add + Promotional Status Bar
 *
 * Free Gift Logic (unchanged):
 *   IF trigger variant exists in cart AND free variant does NOT exist → add once
 *   ELSE → do nothing
 *   No quantity sync. No removal. Shopify handles discount eligibility.
 *
 * Status Bar Logic:
 *   Iterates rules in order (Rule 1 → 2 → 3 → 4). For each rule with enableStatusBar=true:
 *     - Hidden:   trigger variant not in cart at all
 *     - Progress: trigger in cart, qty < triggerQuantity required
 *     - Success:  trigger qty met AND free gift variant is in cart
 *   Shows the FIRST matching rule only. Supports [remaining_qty] and [offer_name] placeholders.
 *
 * Rules are injected by js-variables.liquid → window.freeGiftRules
 * Style config is injected by js-variables.liquid → window.freeGiftStatusBarConfig
 *
 * Cart refresh strategy:
 *   After adding a free gift we call publish(PUB_SUB_EVENTS.cartUpdate) so the theme's own
 *   onCartUpdate() handles the drawer/count refresh (it uses replaceWith() on the DOM node).
 *   Because replaceWith() discards the old status bar node we re-run updateStatusBar()
 *   with a short delay once the theme's async fetch has settled, targeting the NEW DOM nodes.
 */
(function () {
  'use strict';

  // ── Guard: bail if no rules ───────────────────────────────────────

  var rules = window.freeGiftRules;
  if (!rules || !rules.length) return;

  rules = rules.filter(function (r) {
    return r.triggerVariantId && r.freeVariantId;
  });
  if (!rules.length) return;

  // Normalize all fields
  rules.forEach(function (r) {
    r.triggerVariantId  = String(r.triggerVariantId).trim();
    r.freeVariantId     = String(r.freeVariantId).trim();
    r.triggerQuantity   = parseInt(r.triggerQuantity, 10) || 1;
    r.freeQuantity      = parseInt(r.freeQuantity, 10) || 1;
    r.enableStatusBar   = r.enableStatusBar !== false; // default true
    r.offerName         = r.offerName || '';
    r.progressMessage   = r.progressMessage || '';
    r.successMessage    = r.successMessage || '';
  });

  var FREE_GIFT_PROPERTY = '_free_gift';
  var _processing = false;
  var _scheduledTimer = null;

  // ── Status bar: apply theme style config ──────────────────────────

  /**
   * Write CSS custom properties on :root from window.freeGiftStatusBarConfig.
   * Called once on init so all status bar instances pick up the right colors.
   */
  function applyStatusBarStyles() {
    var config = window.freeGiftStatusBarConfig;
    if (!config) return;
    var root = document.documentElement;
    if (config.bgColor)              root.style.setProperty('--promo-bar-bg',         config.bgColor);
    if (config.textColor)            root.style.setProperty('--promo-bar-text',        config.textColor);
    if (config.borderColor)          root.style.setProperty('--promo-bar-border',      config.borderColor);
    if (config.borderRadius != null) root.style.setProperty('--promo-bar-radius',      config.borderRadius + 'px');
    if (config.iconSize     != null) root.style.setProperty('--promo-bar-icon-size',   config.iconSize + 'px');
  }

  // ── Cart API helpers ──────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); });
  }

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId, 10),
          quantity: quantity,
          properties: { _free_gift: 'true' }
        }]
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('Add to cart failed: ' + res.status);
      return res.json();
    });
  }

  // ── Cart UI refresh ───────────────────────────────────────────────

  /**
   * Tell the theme that the cart changed.
   *
   * publish(PUB_SUB_EVENTS.cartUpdate) triggers the theme's own onCartUpdate():
   *   - CartDrawerItems.onCartUpdate() re-fetches ?section_id=cart-drawer and
   *     calls replaceWith() on the entire <cart-drawer-items> node (and footer).
   *     This also re-renders the cart count bubble inside .cart-drawer__footer.
   *   - CartItems.onCartUpdate() re-fetches ?section_id=main-cart-items and
   *     replaces innerHTML (node itself survives).
   *
   * IMPORTANT: replaceWith() on the cart-drawer-items node discards our status bar DOM node.
   * The caller (processCart) must call updateStatusBar() again AFTER a delay that allows
   * the theme's async fetch + replaceWith() to complete.
   */
  function triggerThemeCartRefresh() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-free-gift' });
    }
  }

  // ── Cart item helpers ─────────────────────────────────────────────

  /**
   * Count total quantity of the trigger variant in cart,
   * excluding any lines that are themselves free gifts.
   */
  function getTriggerQty(cartItems, triggerVariantId) {
    var total = 0;
    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      if (
        String(item.variant_id) === triggerVariantId &&
        !(item.properties && item.properties[FREE_GIFT_PROPERTY] === 'true')
      ) {
        total += item.quantity;
      }
    }
    return total;
  }

  /**
   * Returns true if the trigger variant exists in cart (non-free-gift lines only).
   */
  function isTriggerInCart(cartItems, triggerVariantId) {
    return getTriggerQty(cartItems, triggerVariantId) > 0;
  }

  /**
   * Returns true if the free gift variant (marked with _free_gift property) is in cart.
   */
  function isFreeGiftInCart(cartItems, freeVariantId) {
    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      if (
        String(item.variant_id) === freeVariantId &&
        item.properties &&
        item.properties[FREE_GIFT_PROPERTY] === 'true'
      ) {
        return true;
      }
    }
    return false;
  }

  // ── Free gift core logic ──────────────────────────────────────────

  /**
   * For each rule: if trigger is in cart and free gift is NOT → add it once.
   * Returns an array of add actions (may be empty).
   */
  function computeActions(cartItems) {
    var actions = [];
    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];
      if (
        isTriggerInCart(cartItems, rule.triggerVariantId) &&
        !isFreeGiftInCart(cartItems, rule.freeVariantId)
      ) {
        actions.push({ variantId: rule.freeVariantId, quantity: rule.freeQuantity });
      }
    }
    return actions;
  }

  /**
   * Execute add actions sequentially.
   */
  function executeActions(actions, index) {
    if (index >= actions.length) return Promise.resolve();
    return addToCart(actions[index].variantId, actions[index].quantity)
      .then(function () { return executeActions(actions, index + 1); })
      .catch(function (err) {
        console.warn('[AutoFreeGift] Failed to add free gift:', err.message || err);
        return executeActions(actions, index + 1);
      });
  }

  // ── Status bar logic ──────────────────────────────────────────────

  /**
   * Substitute [remaining_qty] and [offer_name] placeholders in a message string.
   */
  function resolvePlaceholders(message, remainingQty, offerName) {
    return message
      .replace(/\[remaining_qty\]/g, String(remainingQty))
      .replace(/\[offer_name\]/g, offerName || '');
  }

  /**
   * Determine the status bar state for the current cart items array.
   * Iterates rules in priority order (Rule 1 first). Returns the FIRST matching rule.
   *
   * Return values:
   *   { show: false }
   *   { show: true, state: 'progress', message: '...' }
   *   { show: true, state: 'success',  message: '...' }
   */
  function computeStatusBarState(cartItems) {
    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];

      if (!rule.enableStatusBar) continue;
      if (!rule.progressMessage && !rule.successMessage) continue;

      var triggerQty = getTriggerQty(cartItems, rule.triggerVariantId);

      // Trigger not in cart → bar hidden for this rule, check next rule
      if (triggerQty === 0) continue;

      var freeExists = isFreeGiftInCart(cartItems, rule.freeVariantId);
      var qualified  = (triggerQty >= rule.triggerQuantity) && freeExists;

      if (qualified) {
        return {
          show:    true,
          state:   'success',
          message: resolvePlaceholders(rule.successMessage, 0, rule.offerName)
        };
      } else {
        var remaining = Math.max(0, rule.triggerQuantity - triggerQty);
        return {
          show:    true,
          state:   'progress',
          message: resolvePlaceholders(rule.progressMessage, remaining, rule.offerName)
        };
      }
    }
    return { show: false };
  }

  /**
   * Update every status bar container in the DOM ([data-cart-promo-status]).
   *
   * NOTE: After the theme's onCartUpdate() runs replaceWith() on cart-drawer-items,
   * the old status bar node is gone. This function always uses querySelectorAll to
   * find the CURRENT nodes, so calling it after the DOM swap targets the new ones.
   */
  function updateStatusBar(cartItems) {
    var result = computeStatusBarState(cartItems);
    var bars   = document.querySelectorAll('[data-cart-promo-status]');

    for (var i = 0; i < bars.length; i++) {
      var bar   = bars[i];
      var msgEl = bar.querySelector('.cart-promo-status__message');

      if (!result.show || !result.message) {
        bar.hidden = true;
        continue;
      }

      if (msgEl) msgEl.textContent = result.message;
      bar.hidden = false;

      // Re-apply CSS variables in case this is a freshly swapped-in node
      applyStatusBarStyles();
    }
  }

  // ── Main process ──────────────────────────────────────────────────

  /**
   * Full lifecycle:
   *   1. Fetch cart state
   *   2. Update status bar with current state
   *   3. If any free gifts need adding → add them
   *   4. Tell the theme the cart changed (triggers drawer + count bubble refresh)
   *   5. Re-fetch cart (post-add) to get accurate item list
   *   6. Wait for the theme's async DOM swap to settle (replaceWith completes)
   *   7. Update status bar again — now targeting the NEW DOM nodes
   */
  function processCart() {
    if (_processing) return;
    _processing = true;

    fetchCart()
      .then(function (cart) {
        // Step 2: show current status immediately (covers the no-action path)
        updateStatusBar(cart.items);

        var actions = computeActions(cart.items);

        // No gifts to add → nothing more to do
        if (!actions.length) {
          _processing = false;
          return;
        }

        // Step 3: add free gifts
        return executeActions(actions, 0)
          .then(function () {
            // Step 4: notify theme → triggers onCartUpdate() → drawer + bubble refresh
            triggerThemeCartRefresh();

            // Step 5: re-fetch cart to get post-add item list
            return fetchCart();
          })
          .then(function (freshCart) {
            // Step 6 + 7:
            // onCartUpdate() is async (fetch + DOM swap). We wait 800 ms — enough for the
            // theme's section fetch to complete and replaceWith() to run — then update the
            // status bar against the NEW nodes that are now in the DOM.
            var freshItems = freshCart.items;
            setTimeout(function () {
              updateStatusBar(freshItems);
              _processing = false;
            }, 800);
          });
      })
      .catch(function (err) {
        console.warn('[AutoFreeGift] Error processing cart:', err.message || err);
        _processing = false;
      });
  }

  /**
   * Debounced — collapses rapid-fire cart events into a single processCart() call.
   */
  function scheduleCartCheck() {
    if (_scheduledTimer) clearTimeout(_scheduledTimer);
    _scheduledTimer = setTimeout(function () {
      _scheduledTimer = null;
      processCart();
    }, 600);
  }

  // ── Init ──────────────────────────────────────────────────────────

  applyStatusBarStyles();

  // ── Event listeners ───────────────────────────────────────────────

  // Theme PubSub cart update — skip events we ourselves published to avoid loops
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'auto-free-gift') return;
      scheduleCartCheck();
    });
  }

  // Custom DOM event dispatched by CartItems.updateQuantity (cart.js line 187)
  document.addEventListener('cartUpdated', function () {
    scheduleCartCheck();
  });

  // Run once on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(processCart, 800);
    });
  } else {
    setTimeout(processCart, 800);
  }

})();
